import { FormFeedBack, ModalBase, removeCpfMask } from '@repo/ui';
import { CpfInput } from 'adasi_components';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useEffect, useState } from 'react';
import { Col, Form, Label, Row } from 'reactstrap';
import * as Yup from 'yup';

interface IModalChangePersonCpf {
  isOpen: boolean;
  personHashId: string;
  currentCpf: string;
  toggleModal: () => void;
  onChanged: () => void;
}

export const ModalChangePersonCpf = ({
  isOpen,
  personHashId,
  currentCpf,
  toggleModal,
  onChanged,
}: IModalChangePersonCpf) => {
  const [loading, setLoading] = useState<boolean>(false);

  const { personService } = useService();

  const validation = useFormik({
    initialValues: {
      cpf: '',
    },
    validationSchema: Yup.object({
      cpf: Yup.string()
        .required('Campo obrigatório')
        .cpfType('CPF inválido')
        .test(
          'is-different',
          'O CPF informado é igual ao atual.',
          (value) =>
            !value || removeCpfMask(value) !== removeCpfMask(currentCpf),
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const res = await personService.changePersonCpf({
          personHashId,
          cpf: removeCpfMask(values.cpf),
        });

        notifySuccess(res.message);
        toggleModal();
        onChanged();
      } catch (error) {
        if (error instanceof Error) {
          notifyError(error.message);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) validation.resetForm();
  }, [isOpen]);

  return (
    <ModalBase.Wrapper
      size="sm"
      isOpen={isOpen}
      loading={loading}
      toggle={toggleModal}
      onClosed={validation.resetForm}
    >
      <ModalBase.Header toggle={toggleModal} withBorderBottom>
        Alterar CPF
      </ModalBase.Header>

      <Form autoComplete="off" onSubmit={validation.handleSubmit}>
        <ModalBase.Body>
          <div className="d-flex flex-column gap-3">
            <div className="alert alert-warning d-flex align-items-start gap-2 mb-0">
              <i className="ri-error-warning-line fs-5 mt-1 flex-shrink-0" />
              <div>
                <strong>O CPF é usado para entrar no sistema.</strong>
                <p className="mb-0 mt-1">
                  Depois da alteração, esta pessoa passa a acessar o Rade com o
                  novo CPF. O CPF atual ({currentCpf}) deixa de funcionar no
                  login.
                </p>
              </div>
            </div>

            <Row>
              <Col md={12}>
                <Label htmlFor="newCpf" className="form-label">
                  Novo CPF
                </Label>
                {/* O CpfInput monta o evento com `name: inputId`, entao
                    `validation.handleChange` gravaria no campo "newCpf". O
                    inputId precisa ser unico na pagina (o formulario atras do
                    modal ja usa id "cpf"), por isso o campo e escrito
                    explicitamente via setFieldValue. */}
                <CpfInput
                  name="cpf"
                  inputId="newCpf"
                  className="form-control"
                  placeholder="Informe o novo CPF"
                  value={validation.values.cpf}
                  handleChange={(event) =>
                    validation.setFieldValue('cpf', event.target.value)
                  }
                  onBlur={validation.handleBlur}
                  invalid={!!(validation.touched.cpf && validation.errors.cpf)}
                />
                <FormFeedBack
                  error={String(validation.errors?.cpf)}
                  isInvalid={
                    !!(validation.touched?.cpf && validation.errors?.cpf)
                  }
                />
              </Col>
            </Row>
          </div>
        </ModalBase.Body>

        <ModalBase.Footer>
          <ModalBase.Actions
            loading={loading}
            goBack={toggleModal}
            actionBtnType="submit"
            actionBtnColor="warning"
            confirmBtnLabel="Alterar CPF"
          />
        </ModalBase.Footer>
      </Form>
    </ModalBase.Wrapper>
  );
};
